import React, { useState, useEffect, Suspense, lazy } from "react";
import { Header } from "./components/Header";
import { JiraConnection } from "./components/JiraConnection";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SkeletonLoader } from "./components/SkeletonLoader";
import { CommandPalette } from "./components/CommandPalette";
import { UniversalSearch } from "./components/UniversalSearch";
import { QuickActions } from "./components/QuickActions";
import { useJiraData } from "./hooks/useJiraData";
import { useConnectJiraData } from "./hooks/useConnectJiraData";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import { connectContext } from "./services/connectContext";
import { useTheme } from "./hooks/useTheme";
import { mockEpics, mockStories, mockSprints } from "./data/mockData";
import { exportAllDataToJSON } from "./utils/export";

// Lazy load heavy components for code splitting
const Timeline = lazy(() => import("./components/Timeline").then(m => ({ default: m.Timeline })));
const Analytics = lazy(() => import("./components/Analytics").then(m => ({ default: m.Analytics })));
const WorkLog = lazy(() => import("./components/WorkLog").then(m => ({ default: m.WorkLog })));
const Settings = lazy(() => import("./components/Settings").then(m => ({ default: m.Settings })));

function App() {
  const [activeView, setActiveView] = useState("timeline");
  // Start with mock data enabled for immediate visual feedback
  const [useMockData, setUseMockData] = useState(true);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const jiraData = useJiraData();
  const connectJiraData = useConnectJiraData();

  // Use Connect data if in Connect environment, otherwise use regular API or mock data
  const activeJiraData = connectContext.isConnectEnvironment()
    ? connectJiraData
    : jiraData;
  useTheme(); // Initialize theme

  // Use mock data or real Jira data based on connection status
  const epics = useMockData ? mockEpics : activeJiraData.epics;
  const stories = useMockData ? mockStories : activeJiraData.stories;
  const sprints = useMockData ? mockSprints : activeJiraData.sprints;

  // Keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: 'k',
      meta: true,
      action: () => setCommandPaletteOpen(true),
      description: 'Open command palette',
    },
    {
      key: 'f',
      meta: true,
      action: () => {
        // Universal Search manages its own state via keyboard listener
      },
      description: 'Universal search',
    },
    {
      key: 'r',
      meta: true,
      action: () => activeJiraData.refreshData(),
      description: 'Refresh data',
    },
    {
      key: '1',
      meta: true,
      action: () => setActiveView('timeline'),
      description: 'Go to Timeline',
    },
    {
      key: '2',
      meta: true,
      action: () => setActiveView('analytics'),
      description: 'Go to Analytics',
    },
    {
      key: '3',
      meta: true,
      action: () => setActiveView('worklog'),
      description: 'Go to Work Log',
    },
    {
      key: '4',
      meta: true,
      action: () => setActiveView('settings'),
      description: 'Go to Settings',
    },
  ]);

  // Preload components based on user behavior
  useEffect(() => {
    // Preload components after initial render
    const preloadTimer = setTimeout(() => {
      // Preload next likely component based on current view
      switch (activeView) {
        case "timeline":
          import("./components/Analytics"); // Preload Analytics
          break;
        case "analytics":
          import("./components/WorkLog"); // Preload WorkLog
          break;
        case "worklog":
          import("./components/Settings"); // Preload Settings
          break;
        case "settings":
          import("./components/Timeline"); // Preload Timeline
          break;
      }
    }, 2000); // Preload after 2 seconds

    return () => clearTimeout(preloadTimer);
  }, [activeView]);

  const renderActiveView = () => {
    // Show connection screen if not connected and not using mock data (only in non-Connect environment)
    if (
      !useMockData &&
      !activeJiraData.connected &&
      !connectContext.isConnectEnvironment()
    ) {
      return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <div className="text-center mb-8">
              <h2 className="apple-title2 text-system-label mb-4">
                Connect to Your Jira Instance
              </h2>
              <p className="apple-body text-system-label-secondary">
                Connect to your Jira project to sync real data, or continue with
                demo data
              </p>
            </div>

            <div className="apple-card apple-card-glass p-8 mb-6">
              <ErrorBoundary enableRetry showDetails>
                <JiraConnection
                  onConnect={
                    activeJiraData.connect || (() => Promise.resolve())
                  }
                  onDisconnect={activeJiraData.disconnect || (() => {})}
                  connected={activeJiraData.connected}
                  loading={activeJiraData.loading}
                  error={activeJiraData.error}
                />
              </ErrorBoundary>
            </div>

            <div className="text-center">
              <button
                onClick={() => setUseMockData(true)}
                className="apple-button apple-button-ghost text-system-label hover:text-system-label"
              >
                Continue with demo data instead
              </button>
            </div>
          </div>
        </div>
      );
    }

    switch (activeView) {
      case "timeline":
        return (
          <ErrorBoundary
            enableRetry
            showDetails
            fallback={
              <div className="p-6 text-center">
                <h3 className="apple-headline text-system-label mb-2">
                  Timeline Unavailable
                </h3>
                <p className="apple-body text-system-label-secondary">
                  The timeline view encountered an error. Please try again.
                </p>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="p-6">
                  <SkeletonLoader variant="timeline" />
                </div>
              }
            >
              <Timeline epics={epics} stories={stories} sprints={sprints} />
            </Suspense>
          </ErrorBoundary>
        );
      case "analytics":
        return (
          <ErrorBoundary
            enableRetry
            showDetails
            fallback={
              <div className="p-6 text-center">
                <h3 className="apple-headline text-system-label mb-2">
                  Analytics Unavailable
                </h3>
                <p className="apple-body text-system-label-secondary">
                  The analytics view encountered an error. Please try again.
                </p>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="p-6 space-y-6">
                  <SkeletonLoader variant="chart" count={2} />
                  <SkeletonLoader variant="card" count={3} />
                </div>
              }
            >
              <Analytics epics={epics} stories={stories} sprints={sprints} />
            </Suspense>
          </ErrorBoundary>
        );
      case "worklog":
        return (
          <ErrorBoundary
            enableRetry
            showDetails
            fallback={
              <div className="p-6 text-center">
                <h3 className="apple-headline text-system-label mb-2">
                  Work Log Unavailable
                </h3>
                <p className="apple-body text-system-label-secondary">
                  The work log view encountered an error. Please try again.
                </p>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="p-6">
                  <SkeletonLoader variant="card" count={5} />
                </div>
              }
            >
              <WorkLog
                stories={stories}
                onLogWork={
                  activeJiraData.connected ? activeJiraData.logWork : undefined
                }
              />
            </Suspense>
          </ErrorBoundary>
        );
      case "settings":
        return (
          <ErrorBoundary
            enableRetry
            showDetails
            fallback={
              <div className="p-6 text-center">
                <h3 className="apple-headline text-system-label mb-2">
                  Settings Unavailable
                </h3>
                <p className="apple-body text-system-label-secondary">
                  The settings view encountered an error. Please try again.
                </p>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="p-6">
                  <SkeletonLoader variant="card" count={4} />
                </div>
              }
            >
              <Settings
                jiraConnection={{
                  connected: activeJiraData.connected,
                  loading: activeJiraData.loading,
                  error: activeJiraData.error,
                  onConnect:
                    activeJiraData.connect || (() => Promise.resolve()),
                  onDisconnect: activeJiraData.disconnect || (() => {}),
                  onRefresh: activeJiraData.refreshData,
                }}
                useMockData={useMockData}
                onToggleMockData={setUseMockData}
              />
            </Suspense>
          </ErrorBoundary>
        );
      default:
        return (
          <ErrorBoundary enableRetry>
            <Suspense
              fallback={
                <div className="p-6">
                  <SkeletonLoader variant="timeline" />
                </div>
              }
            >
              <Timeline epics={epics} stories={stories} sprints={sprints} />
            </Suspense>
          </ErrorBoundary>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f7] apple-scrollbar">
      <ErrorBoundary
        enableRetry
        fallback={
          <div className="h-16 glass-heavy flex items-center justify-center">
            <p className="apple-body text-system-label-secondary">
              Navigation temporarily unavailable
            </p>
          </div>
        }
      >
        <Header
          activeView={activeView}
          onViewChange={setActiveView}
          jiraConnected={activeJiraData.connected}
          useMockData={useMockData}
        />
      </ErrorBoundary>

      {/* Main content with proper top padding for fixed header */}
      <main className="relative min-h-screen pt-20">
        {/* Sync notification with glassmorphism */}
        {activeJiraData.loading && (
          <div className="fixed top-24 right-6 glass-heavy apple-card-glass p-4 z-50 animate-apple-fade-in shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 border-2 border-system-label border-t-transparent rounded-full animate-apple-spin" />
              <span className="apple-callout text-system-label font-medium">
                Syncing with Jira...
              </span>
            </div>
          </div>
        )}

        <div className="relative z-10 pb-8">{renderActiveView()}</div>
      </main>

      {/* Command Palette - ⌘K */}
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        onNavigate={(view) => {
          setActiveView(view);
          setCommandPaletteOpen(false);
        }}
      />

      {/* Universal Search - ⌘F */}
      <UniversalSearch
        epics={epics}
        stories={stories}
        sprints={sprints}
        onSelect={(result) => {
          console.log('Selected:', result);
          // Navigate to relevant view based on result type
          if (result.type === 'epic' || result.type === 'story') {
            setActiveView('timeline');
          }
        }}
      />

      {/* Quick Actions - Floating Menu */}
      <QuickActions
        onRefresh={() => activeJiraData.refreshData()}
        onExport={() => {
          // Export all data as JSON
          exportAllDataToJSON(epics, stories, sprints);
        }}
        onShare={() => {
          const url = window.location.href;
          navigator.clipboard.writeText(url);
          console.log('Link copied to clipboard');
        }}
        onFilter={() => console.log('Filter clicked')}
        onSort={() => console.log('Sort clicked')}
      />
    </div>
  );
}

export default App;
